import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, X, Calendar, FileText, Gift, CreditCard, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import useRealtime from '../hooks/useRealtime';

// How often to refresh the unread count when the dropdown is closed.
// Keep it low (30s) — payroll/leave events are not high-frequency, and we
// don't want to hammer the API.
const POLL_MS = 30000;

const TYPE_META = {
  leave_request:     { icon: Calendar,   tint: 'from-amber-400 to-orange-500' },
  leave_approved:    { icon: Calendar,   tint: 'from-emerald-400 to-teal-500' },
  leave_rejected:    { icon: Calendar,   tint: 'from-rose-400 to-pink-500' },
  payroll_processed: { icon: FileText,   tint: 'from-indigo-500 to-violet-500' },
  bonus_added:       { icon: Gift,       tint: 'from-fuchsia-400 to-pink-500' },
  advance_created:   { icon: CreditCard, tint: 'from-sky-400 to-blue-500' },
  info:              { icon: Info,       tint: 'from-slate-400 to-slate-500' },
};

const formatRelative = (iso) => {
  const d = new Date(iso);
  const diff = Math.max(0, Date.now() - d.getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Guard against setState after unmount — if the user logs out mid-poll,
  // the in-flight response would otherwise warn about updating an unmounted
  // component.
  const mountedRef = useRef(true);

  const fetchUnread = async () => {
    try {
      const { data } = await api.get('/notifications/unread-count');
      if (mountedRef.current) setUnread(data.count || 0);
    } catch { /* silent — bell is non-critical UX */ }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications', { params: { limit: 20 } });
      if (mountedRef.current) setItems(data.items || []);
    } catch {
      if (mountedRef.current) setItems([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // Poll unread count on an interval, but ONLY while the tab is visible.
  // Backgrounded tabs shouldn't burn API calls every 30s, and most browsers
  // already throttle setInterval in hidden tabs anyway — explicit pause keeps
  // server-side load predictable. Re-fetch immediately on visibility regain
  // so the badge is fresh when the user returns.
  useEffect(() => {
    mountedRef.current = true;
    let timer = null;
    const start = () => {
      if (timer) return;
      fetchUnread();
      timer = setInterval(fetchUnread, POLL_MS);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      mountedRef.current = false;
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, []);

  // Refresh both list and badge count each time the dropdown opens — another
  // tab may have marked things read while this one was idle.
  useEffect(() => {
    if (!open) return;
    fetchList();
    fetchUnread();
  }, [open]);

  // Realtime — listen for live notifications pushed from the server and
  // (a) bump the unread counter without the 30s poll lag, (b) surface a
  // toast so the user notices even with the dropdown closed. Refresh the
  // list as well if the dropdown happens to be open.
  const socket = useRealtime();
  useEffect(() => {
    if (!socket) return undefined;
    const handler = (payload) => {
      if (mountedRef.current) {
        setUnread((u) => u + 1);
        if (open) fetchList();
      }
      toast(payload?.title || 'New notification', {
        description: payload?.message || undefined,
      });
    };
    socket.on('notification', handler);
    return () => socket.off('notification', handler);
  }, [socket, open]);

  const handleItemClick = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      // Optimistic update — don't wait for the server before navigating.
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/notifications/${n.id}/read`).catch(() => { /* best-effort */ });
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    if (unread === 0) return;
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    setUnread(0);
    try { await api.post('/notifications/read-all'); } catch { /* best-effort */ }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    const was = items.find((x) => x.id === id);
    setItems((prev) => prev.filter((x) => x.id !== id));
    if (was && !was.is_read) setUnread((u) => Math.max(0, u - 1));
    try { await api.delete(`/notifications/${id}`); } catch { /* best-effort */ }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="p-1.5 rounded-lg hover:bg-gradient-to-br hover:from-indigo-50 hover:to-violet-50 transition-colors relative"
        aria-label="Notifications"
      >
        <Bell size={18} className="text-slate-500" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center shadow-sm"
            style={{ background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)' }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Click-outside backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          <div
            // Anchored to the bell's right edge, so a fixed 360px panel runs off
            // the left of a ~360px phone. Clamp it to the viewport minus the
            // header gutters, and cap the list against the visible height.
            className="absolute right-0 top-10 z-50 w-[calc(100vw-1.5rem)] max-w-[360px] rounded-xl overflow-hidden border border-slate-200/70 shadow-[0_24px_48px_-12px_rgba(99,102,241,0.25)]"
            style={{ background: 'linear-gradient(135deg, #ffffff 0%, #fafbff 100%)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/70"
                 style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)' }}>
              <div>
                <p className="text-sm font-bold text-slate-900">Notifications</p>
                <p className="text-[10px] text-slate-500">
                  {unread > 0 ? `${unread} unread` : 'You’re all caught up'}
                </p>
              </div>
              {unread > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-[min(420px,60dvh)] overflow-y-auto overscroll-contain">
              {loading && items.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-slate-400">Loading…</div>
              ) : items.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-2"
                       style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)' }}>
                    <Bell size={20} className="text-indigo-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-700">No notifications yet</p>
                  <p className="text-xs text-slate-400 mt-0.5">We&rsquo;ll let you know when something happens.</p>
                </div>
              ) : (
                items.map((n) => {
                  const meta = TYPE_META[n.type] || TYPE_META.info;
                  const Icon = meta.icon;
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleItemClick(n)}
                      className={`group w-full flex items-start gap-3 px-3 py-3 border-b border-slate-100/80 text-left transition-colors hover:bg-gradient-to-r hover:from-indigo-50/60 hover:to-violet-50/40 ${
                        !n.is_read ? 'bg-gradient-to-r from-indigo-50/40 to-transparent' : ''
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br ${meta.tint} shadow-sm flex-shrink-0`}>
                        <Icon size={16} className="text-white" strokeWidth={2.2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <p className={`text-sm leading-tight ${!n.is_read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                            {n.title}
                          </p>
                          {!n.is_read && (
                            <span className="w-2 h-2 mt-1 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex-shrink-0" />
                          )}
                        </div>
                        {n.message && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">{formatRelative(n.created_at)}</p>
                      </div>
                      <span
                        onClick={(e) => handleDelete(e, n.id)}
                        role="button"
                        tabIndex={0}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-opacity"
                        title="Dismiss"
                      >
                        <X size={13} />
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="px-4 py-2 border-t border-slate-200/70 text-center">
                <p className="text-[10px] text-slate-400">
                  Showing latest {items.length} {items.length === 1 ? 'notification' : 'notifications'}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
